/** Resize and encode a browser-decodable image before sending any bytes. */
export async function prepareImageForUpload(
  file: File,
  { maxDimension, maxBytes }: { maxDimension: number; maxBytes: number },
): Promise<File> {
  const sourceUrl = URL.createObjectURL(file);
  const image = new Image();
  const canvas = document.createElement('canvas');

  try {
    image.src = sourceUrl;
    try {
      await image.decode();
    } catch {
      throw new Error(
        'This photo could not be read. Try a JPEG, PNG, or WebP image.',
      );
    }
    if (!image.naturalWidth || !image.naturalHeight) {
      throw new Error('This photo could not be read. Try another image.');
    }
    const context = canvas.getContext('2d');
    if (!context)
      throw new Error(
        'Photo processing is unavailable. Please try another browser.',
      );

    const scale = Math.min(
      1,
      maxDimension / Math.max(image.naturalWidth, image.naturalHeight),
    );
    let width = Math.max(1, Math.round(image.naturalWidth * scale));
    let height = Math.max(1, Math.round(image.naturalHeight * scale));

    for (let attempt = 0; attempt < 4; attempt += 1) {
      canvas.width = width;
      canvas.height = height;
      // JPEG has no alpha channel; preserve transparent images on white.
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);
      for (const quality of [0.85, 0.7, 0.55, 0.4]) {
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, 'image/jpeg', quality),
        );
        if (!blob || blob.type !== 'image/jpeg' || !blob.size) {
          throw new Error(
            'This photo could not be processed. Try another image.',
          );
        }
        if (blob.size <= maxBytes) {
          const basename = file.name.replace(/\.[^.]+$/, '') || 'photo';
          return new File([blob], `${basename}.jpg`, { type: 'image/jpeg' });
        }
      }
      width = Math.max(1, Math.floor(width * 0.75));
      height = Math.max(1, Math.floor(height * 0.75));
    }
    throw new Error(
      'This photo is still too large after compression. Try another image.',
    );
  } finally {
    URL.revokeObjectURL(sourceUrl);
    image.src = '';
    canvas.width = 0;
    canvas.height = 0;
  }
}
